import React, { useCallback, useEffect, useState } from 'react';
import { Trash2, Plus, Copy, QrCode, X, Mail, Smartphone, KeyRound, Pencil, Check } from 'lucide-react';
import Layout from '../components/Layout';
import api, { formatError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ROLES = ['employee', 'manager', 'boss'];
const roleBg = { boss: 'bg-black text-white', manager: 'bg-[#FFD700] text-zinc-900', employee: 'bg-zinc-100 text-zinc-900' };

export default function Users() {
  const { user, workspace, setWorkspace } = useAuth();
  const isBoss = user?.role === 'boss';
  const [tab, setTab] = useState('members');
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [newInvite, setNewInvite] = useState(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviteEmail, setInviteEmail] = useState('');
  const [directForm, setDirectForm] = useState({ name: '', email: '', role: 'employee', temp_password: '' });
  const [directResult, setDirectResult] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([api.get('/users'), api.get('/invites')]);
      setUsers(u.data); setInvites(i.data);
    } catch (err) { toast.error(formatError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeUser = async (id) => {
    if (!window.confirm('Remove this user?')) return;
    try { await api.delete(`/users/${id}`); toast.success('User removed'); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const resetUserPassword = async (target) => {
    if (!window.confirm(`Reset password for ${target.name}? A new temp password will be generated.`)) return;
    try {
      const { data } = await api.post(`/users/${target.id}/reset-password`);
      setResetResult({ ...data, name: target.name });
      toast.success(`Password reset for ${target.name}`);
    } catch (err) { toast.error(formatError(err)); }
  };

  const createInvite = async (e) => {
    e?.preventDefault();
    setCreatingInvite(true);
    try {
      const { data } = await api.post('/invites', { role: inviteRole, email: inviteEmail || null, expires_in_days: 14 });
      setNewInvite(data);
      setInviteEmail('');
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setCreatingInvite(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    try { await api.delete(`/invites/${id}`); toast.success('Invite revoked'); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  const changeRole = async (target, newRole) => {
    if (target.role === newRole) return;
    try {
      await api.patch(`/users/${target.id}/role`, { role: newRole });
      toast.success(`${target.name} is now ${newRole}`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const directAdd = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/users/direct', directForm);
      setDirectResult(data);
      setDirectForm({ name: '', email: '', role: 'employee', temp_password: '' });
      toast.success('User created');
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const genTempPassword = () => {
    const s = Math.random().toString(36).slice(-8);
    setDirectForm({ ...directForm, temp_password: s });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">TEAM ADMINISTRATION</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">PEOPLE</h1>
          </div>
          {isBoss && workspace && <WorkspaceRenamer workspace={workspace} setWorkspace={setWorkspace} />}
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 border border-zinc-900 max-w-2xl">
          {[
            { k: 'members', label: 'Members', count: users.length },
            { k: 'invites', label: 'Invites', count: invites.filter(i => !i.used_at && i.expires_at > new Date().toISOString()).length },
            { k: 'add', label: 'Add Person' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${tab === t.k ? 'bg-zinc-900 text-white' : 'bg-white hover:bg-zinc-100'}`}
            >
              {t.label}{t.count !== undefined ? ` (${t.count})` : ''}
            </button>
          ))}
        </div>

        {tab === 'members' && (
          <>
            {resetResult && <ResetPasswordCard result={resetResult} onClose={() => setResetResult(null)} />}
            <div className="bg-white border border-zinc-900">
            <div className="grid grid-cols-12 border-b border-zinc-900 bg-zinc-50 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
              <div className="col-span-5">Name / Email</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-3">Joined</div>
              <div className="col-span-1"></div>
            </div>            {users.map((u) => (
              <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-12 border-b border-zinc-100 px-4 py-3 items-center">
                <div className="col-span-5">
                  <div className="font-semibold">{u.name}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </div>
                <div className="col-span-3">
                  {isBoss && u.id !== user.id ? (
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      data-testid={`role-select-${u.id}`}
                      className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-900 outline-none cursor-pointer ${roleBg[u.role]}`}
                    >
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : (
                    <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-900 ${roleBg[u.role]}`}>{u.role}</span>
                  )}
                </div>
                <div className="col-span-3 text-xs font-mono text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
                <div className="col-span-1 flex justify-end gap-1">
                  {u.id !== user.id && (user.role === 'boss' || (user.role === 'manager' && u.role === 'employee')) && (
                    <button onClick={() => resetUserPassword(u)} data-testid={`reset-pwd-${u.id}`} className="text-zinc-500 hover:text-[#002FA7]" title="Reset password"><KeyRound size={14} /></button>
                  )}
                  {isBoss && u.id !== user.id && (
                    <button onClick={() => removeUser(u.id)} data-testid={`delete-user-${u.id}`} className="text-zinc-500 hover:text-red-600" title="Remove user"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          </>
        )}

        {tab === 'invites' && (
          <div className="space-y-4">
            <form onSubmit={createInvite} className="bg-white border border-zinc-900 p-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-1">Email (optional — for tracking)</label>
                <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="If left blank, anyone with the code can use it"
                  className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-1">Role</label>
                <select data-testid="invite-role-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <button type="submit" disabled={creatingInvite} data-testid="create-invite-btn" className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#002088] disabled:opacity-60">
                <Plus size={14} /> {creatingInvite ? 'Creating...' : 'Create Invite'}
              </button>
            </form>

            {newInvite && <InviteShareCard invite={newInvite} onClose={() => setNewInvite(null)} />}

            <div className="bg-white border border-zinc-900">
              <div className="grid grid-cols-12 border-b border-zinc-900 bg-zinc-50 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-600">
                <div className="col-span-3">Code</div>
                <div className="col-span-2">Role</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Expires</div>
              </div>
              {invites.length === 0 && <div className="p-6 text-sm text-zinc-500">No invites yet. Create one above.</div>}
              {invites.map((i) => {
                const status = inviteStatus(i);
                return (
                  <div key={i.id} data-testid={`invite-row-${i.id}`} className="grid grid-cols-12 border-b border-zinc-100 px-4 py-3 items-center text-sm">
                    <div className="col-span-3 font-mono font-bold text-[#002FA7]">{i.code}</div>
                    <div className="col-span-2 text-xs uppercase">{i.role}</div>
                    <div className="col-span-3 text-xs truncate">{i.email || <span className="text-zinc-400">— any —</span>}</div>
                    <div className="col-span-2">
                      <span className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 border ${statusBadge(status)}`}>{status}</span>
                    </div>
                    <div className="col-span-1 text-xs font-mono text-zinc-500">{new Date(i.expires_at).toLocaleDateString()}</div>
                    <div className="col-span-1 flex justify-end">
                      {status === 'ACTIVE' && (
                        <button onClick={() => setNewInvite(i)} data-testid={`share-invite-${i.id}`} className="text-zinc-500 hover:text-zinc-900 mr-2" title="Show share options"><QrCode size={14} /></button>
                      )}
                      <button onClick={() => revokeInvite(i.id)} data-testid={`revoke-invite-${i.id}`} className="text-zinc-500 hover:text-red-600" title="Revoke"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'add' && (
          <div className="bg-white border border-zinc-900 p-6 max-w-2xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">FOR NON-TECH-SAVVY USERS</div>
            <div className="font-display text-2xl font-black tracking-tighter mb-1">Add a person directly</div>
            <p className="text-sm text-zinc-600 mb-5">You set their email + a temporary password. Hand the password to them in person, on paper, or by phone. They can change it later from /forgot-password.</p>
            <form onSubmit={directAdd} className="space-y-4">
              <Field label="Full name" testid="direct-name" value={directForm.name} onChange={(v) => setDirectForm({ ...directForm, name: v })} />
              <Field label="Email" type="email" testid="direct-email" value={directForm.email} onChange={(v) => setDirectForm({ ...directForm, email: v })} />
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Role</label>
                <select data-testid="direct-role" value={directForm.role} onChange={(e) => setDirectForm({ ...directForm, role: e.target.value })} className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Temporary password</label>
                <div className="flex items-stretch gap-0 border border-zinc-300 focus-within:border-[#002FA7]">
                  <input data-testid="direct-pwd" type="text" required minLength={4} value={directForm.temp_password} onChange={(e) => setDirectForm({ ...directForm, temp_password: e.target.value })}
                    className="flex-1 px-3 py-2 outline-none font-mono" placeholder="At least 4 characters" />
                  <button type="button" onClick={genTempPassword} data-testid="gen-pwd-btn" className="bg-zinc-900 text-white px-3 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-700">Generate</button>
                </div>
              </div>
              <button type="submit" data-testid="direct-submit" className="bg-[#002FA7] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088]">
                Create User →
              </button>
            </form>

            {directResult && (
              <div className="mt-6 border border-zinc-900 bg-yellow-50 p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-800 mb-2">USER CREATED — SHARE THESE CREDENTIALS</div>
                <div className="text-sm space-y-1 font-mono">
                  <div><span className="text-zinc-500">Email:</span> <strong>{directResult.user.email}</strong></div>
                  <div><span className="text-zinc-500">Temp password:</span> <strong>{directResult.temp_password}</strong></div>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(`Login URL: ${window.location.origin}/login\nEmail: ${directResult.user.email}\nTemp password: ${directResult.temp_password}`); toast.success('Credentials copied'); }}
                  data-testid="copy-direct-creds" className="mt-3 flex items-center gap-1 border border-zinc-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white">
                  <Copy size={12} /> Copy login instructions
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function InviteShareCard({ invite, onClose }) {
  const url = `${window.location.origin}/join/${invite.code}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(url)}`;
  const smsBody = `Join our team on SyncGrid as ${invite.role}: ${url} (code ${invite.code})`;

  const copy = (val, label) => { navigator.clipboard.writeText(val); toast.success(`${label} copied`); };

  return (
    <div className="bg-white border border-zinc-900 shadow-brutal p-5" data-testid="invite-share-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">SHARE INVITE</div>
          <div className="font-display text-2xl font-black tracking-tighter">For role: {invite.role}</div>
        </div>
        <button onClick={onClose} data-testid="close-share-card"><X size={18} /></button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">CODE (read it out loud)</div>
            <div className="flex items-stretch border border-zinc-900">
              <div data-testid="share-code" className="flex-1 px-3 py-3 font-mono text-2xl font-bold tracking-widest text-[#002FA7]">{invite.code}</div>
              <button onClick={() => copy(invite.code, 'Code')} data-testid="copy-code" className="bg-zinc-900 text-white px-3 hover:bg-zinc-700"><Copy size={14} /></button>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">LINK (paste in WhatsApp / SMS / email)</div>
            <div className="flex items-stretch border border-zinc-900">
              <input data-testid="share-link" readOnly value={url} className="flex-1 px-3 py-2 text-xs font-mono bg-white outline-none" />
              <button onClick={() => copy(url, 'Link')} data-testid="copy-link" className="bg-zinc-900 text-white px-3 hover:bg-zinc-700"><Copy size={14} /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`sms:?body=${encodeURIComponent(smsBody)}`} data-testid="share-sms" className="flex items-center gap-1 border border-zinc-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white">
              <Smartphone size={12} /> Send SMS
            </a>
            <a href={`https://wa.me/?text=${encodeURIComponent(smsBody)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp" className="flex items-center gap-1 border border-zinc-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white">
              WhatsApp
            </a>
            <a href={`mailto:${invite.email || ''}?subject=${encodeURIComponent('Join our team on SyncGrid')}&body=${encodeURIComponent(smsBody)}`} data-testid="share-email" className="flex items-center gap-1 border border-zinc-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white">
              <Mail size={12} /> Email
            </a>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1 self-start">QR CODE (scan with phone camera)</div>
          <img src={qrUrl} alt="QR code" data-testid="invite-qr" className="border border-zinc-900 bg-white" width="220" height="220" />
        </div>
      </div>
      <div className="mt-4 text-xs text-zinc-500 font-mono">Expires: {new Date(invite.expires_at).toLocaleString()}</div>
    </div>
  );
}

function WorkspaceRenamer({ workspace, setWorkspace }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(workspace.name || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === workspace.name) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch('/workspace', { name: trimmed });
      setWorkspace({ id: data.workspace.id, name: data.workspace.name });
      toast.success('Workspace renamed');
      setEditing(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button onClick={() => { setName(workspace.name || ''); setEditing(true); }} data-testid="rename-workspace-btn" className="flex items-center gap-2 border border-zinc-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white transition-colors">
        <Pencil size={12} /> Rename Workspace
      </button>
    );
  }
  return (
    <div className="flex items-stretch gap-0 border border-zinc-900" data-testid="rename-workspace-form">
      <input
        data-testid="workspace-name-input"
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        maxLength={80}
        className="px-3 py-2 outline-none w-56"
      />
      <button onClick={save} disabled={saving} data-testid="workspace-name-save" className="bg-[#002FA7] text-white px-3 hover:bg-[#002088] disabled:opacity-60"><Check size={14} /></button>
      <button onClick={() => setEditing(false)} data-testid="workspace-name-cancel" className="bg-white px-3 border-l border-zinc-900 hover:bg-zinc-100"><X size={14} /></button>
    </div>
  );
}

function ResetPasswordCard({ result, onClose }) {  const instructions = `Login URL: ${window.location.origin}/login\nEmail: ${result.user.email}\nTemp password: ${result.temp_password}\n\n(Please change it after logging in via Change Password)`;
  return (
    <div className="bg-yellow-50 border border-zinc-900 shadow-brutal p-5 mb-4" data-testid="reset-result-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">PASSWORD RESET FOR {result.name?.toUpperCase()}</div>
          <div className="font-display text-xl font-black tracking-tighter">Share these credentials</div>
        </div>
        <button onClick={onClose} data-testid="close-reset-card"><X size={18} /></button>
      </div>
      <div className="text-sm space-y-1 font-mono mb-3">
        <div><span className="text-zinc-500">Email:</span> <strong data-testid="reset-email">{result.user.email}</strong></div>
        <div><span className="text-zinc-500">New temp password:</span> <strong data-testid="reset-temp-pwd">{result.temp_password}</strong></div>
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(instructions); toast.success('Credentials copied'); }}
        data-testid="copy-reset-creds"
        className="flex items-center gap-1 border border-zinc-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white"
      >
        <Copy size={12} /> Copy login instructions
      </button>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">{label}</label>
      <input data-testid={testid} type={type} required value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2 bg-white" />
    </div>
  );
}

function inviteStatus(i) {
  if (i.used_at) return 'USED';
  if (i.expires_at < new Date().toISOString()) return 'EXPIRED';
  return 'ACTIVE';
}

function statusBadge(status) {
  if (status === 'ACTIVE') return 'border-zinc-900 bg-zinc-100';
  if (status === 'USED') return 'bg-black text-white border-black';
  return 'bg-zinc-200 text-zinc-500 border-zinc-300';
}
